import { Button, Card, Flex } from '@aws-amplify/ui-react';
import type { Edulead } from 'edulead';
import { useContext, useEffect, useState } from 'react';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import LoadingBars from '../custom-components/LoadingBars';
import { SalesforceFieldMappingsInline } from '../custom-components/SalesforceFieldMappingsInline';
import UniversalTag from '../custom-components/UniversalTag';
import { GlobalContext } from '../global-context';
import { usePermission } from '../hooks/usePermission';
import { toastV4Error } from '../services/edulead-v4';
import { eduleadV4 } from '../services/edulead-v4-client';
import { SALESFORCE_APP_INSTALL_URL } from '../utils/Constants';
import General from '../utils/GeneralUtils';
import './Integrations.scss';

export function IntegrationSalesforce() {
  const globalContext = useContext(GlobalContext);
  const { hasPermission } = usePermission();
  const canManageConnection = hasPermission('crm-connection:manage');
  const canEditFieldMappings = hasPermission('crm-field-mappings:edit');
  const [isConnectingToSalesforce, setIsConnectingToSalesforce] = useState(false);
  const [salesforceConnectionStatus, setSalesforceConnectionStatus] = useState<Edulead.EduleadV4Integration | null>(null);
  const [isLoadingSalesforceStatus, setIsLoadingSalesforceStatus] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [fieldMappingsKey, setFieldMappingsKey] = useState(0);
  const [salesforceStepCompleted, setSalesforceStepCompleted] = useState({
    appInstalled: false,
    oauthConnected: false,
    ncesIdMapped: false
  });

  useEffect(() => {
    General.setTitle(document, 'Salesforce Integration', globalContext);
  }, [globalContext]);

  useEffect(() => {
    if (globalContext?.authInfo?.teamId) {
      loadConnectionStatus();
    }
  }, [globalContext.authInfo?.teamId]);

  useEffect(() => {
    handleSalesforceCallback();
  }, []);

  async function loadConnectionStatus(showToast = false) {
    try {
      setIsRefreshing(true);
      setIsLoadingSalesforceStatus(true);

      const sfStatus = (await eduleadV4.integrations.get({ id: 'salesforce' })).integration;
      setSalesforceConnectionStatus(sfStatus ?? null);
      setSalesforceStepCompleted({
        appInstalled: sfStatus?.app_installed || false,
        oauthConnected: sfStatus?.connected || false,
        ncesIdMapped: sfStatus?.field_mappings_configured || false
      });

      if (showToast) {
        toast.success('Connection status refreshed');
      }
    } catch (error) {
      console.error('Failed to load Salesforce connection status:', error);
      if (showToast) {
        toast.error('Failed to load Salesforce connection status');
      }
    } finally {
      setIsLoadingSalesforceStatus(false);
      setIsRefreshing(false);
    }
  }

  async function connectToSalesforce() {
    try {
      setIsConnectingToSalesforce(true);
      const response = await eduleadV4.integrations.createAuthorization({
        integration_id: 'salesforce',
        request_id: crypto.randomUUID(),
        body: {},
      });
      window.open(response.integration_authorization?.authorization_url);
      setIsConnectingToSalesforce(false);
    } catch (error) {
      toastV4Error(error, 'Failed to initiate Salesforce connection');
      setIsConnectingToSalesforce(false);
    }
  }

  async function disconnectFromSalesforce() {
    try {
      if (window.confirm('Are you sure you want to disconnect from Salesforce?')) {
        await eduleadV4.integrations.delete({ id: 'salesforce' });
        toast.success('Successfully disconnected from Salesforce');
        await loadConnectionStatus();
      }
    } catch (error) {
      toastV4Error(error, 'Failed to disconnect from Salesforce');
    }
  }

  async function handleSalesforceCallback() {
    // Match both legacy (/settings/salesforce/callback) and new (/settings/integrations/salesforce/callback) paths
    if (!window.location.pathname.includes('/salesforce/callback')) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');

    if (code && state) {
      try {
        setIsConnectingToSalesforce(true);
        await eduleadV4.integrations.connect({ id: 'salesforce', code, state });

        toast.success('Successfully connected to Salesforce!');

        const url = new URL(window.location.href);
        url.searchParams.delete('code');
        url.searchParams.delete('state');
        window.history.replaceState({}, document.title, url.toString());

        await loadConnectionStatus();
        // Remount the field mappings: they mounted on the status read before the connect finished
        // (not connected, so hidden) and do not reload on their own
        setFieldMappingsKey(prev => prev + 1);
      } catch (error) {
        toastV4Error(error, 'Failed to complete Salesforce connection');
      } finally {
        setIsConnectingToSalesforce(false);
      }
    }
  }

  /** Marks the Salesforce package installed or not (the first setup step). */
  async function setAppInstalled(installed: boolean) {
    await eduleadV4.integrations.update({ id: 'salesforce', update_mask: 'app_installed', body: { app_installed: installed } });
  }

  function handleFieldMappingsSaved() {
    // Refresh connection status to update field mapping configuration status
    loadConnectionStatus();
    // Force re-render of field mappings component
    setFieldMappingsKey(prev => prev + 1);
  }

  return (
    <GlobalContext.Consumer>
      {() => (
        <>
          <div className="content content-1000 integrations-page">
            <Flex
              justifyContent="space-between"
              alignItems="center"
              style={{ marginTop: 20, marginBottom: 10 }}
            >
              <h2 style={{ marginTop: 0, marginBottom: 0 }}>Salesforce Integration</h2>
              <Button
                size="small"
                className="tiny"
                onClick={() => loadConnectionStatus(true)}
                disabled={isRefreshing || isLoadingSalesforceStatus}
              >
                {isRefreshing ? 'Refreshing...' : 'Refresh'}
              </Button>
            </Flex>
                        <Card variation='outlined' style={{ fontSize: 14, marginTop: 15, padding: 0 }}>
              {
                isLoadingSalesforceStatus ? (
                  <div style={{ padding: 15 }}>
                    <LoadingBars numberOfRows={6} />
                  </div>
                ) : (
                  <div className="salesforce-setup">
                    {/* Step 1: Install Salesforce App */}
                    <div className={`salesforce-setup-step ${salesforceStepCompleted.appInstalled ? 'salesforce-setup-step--completed' : ''}`}>
                      <div className="salesforce-setup-step-header">
                        <div className={`salesforce-setup-step-number ${salesforceStepCompleted.appInstalled ? 'salesforce-setup-step-number--completed' : ''}`}>
                          1
                        </div>
                        <span className="salesforce-setup-step-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flex: 1 }}>
                          <span>Step 1: Install Edulead App in Salesforce</span>
                          {salesforceStepCompleted.appInstalled ? (
                            <UniversalTag category="DONE" />
                          ) : (
                            <UniversalTag category="PENDING" />
                          )}
                        </span>
                      </div>
                      <div className="salesforce-setup-step-description">
                        Install the Edulead External Client App in your Salesforce organization. Available for Enterprise, Unlimited, Developer, and Performance editions.
                      </div>
                      <div className="salesforce-setup-step-actions">
                        <span
                          data-tooltip-id={!canManageConnection ? 'main-tooltip' : undefined}
                          data-tooltip-content={!canManageConnection ? 'You do not have permission to manage CRM connections' : undefined}
                        >
                          <Button
                            size="small"
                            variation="primary"
                            className='tiny'
                            onClick={() => window.open(SALESFORCE_APP_INSTALL_URL, '_blank')}
                            disabled={!canManageConnection}
                          >
                            Install Edulead App in Salesforce <FaExternalLinkAlt style={{ fontSize: 10, marginLeft: 5 }} />
                          </Button>
                        </span>
                        {salesforceStepCompleted.appInstalled ? (
                          <span
                            data-tooltip-id={!canManageConnection ? 'main-tooltip' : undefined}
                            data-tooltip-content={!canManageConnection ? 'You do not have permission to manage CRM connections' : undefined}
                          >
                            <Button
                              size="small"
                              className='tiny'
                              onClick={async () => {
                                try {
                                  await setAppInstalled(false);
                                  setSalesforceStepCompleted(prev => ({ ...prev, appInstalled: false }));
                                } catch (error) {
                                  toastV4Error(error, 'Failed to update app installation status');
                                }
                              }}
                              disabled={!canManageConnection}
                            >
                              Mark as Not Done
                            </Button>
                          </span>
                        ) : (
                          <span
                            data-tooltip-id={!canManageConnection ? 'main-tooltip' : undefined}
                            data-tooltip-content={!canManageConnection ? 'You do not have permission to manage CRM connections' : undefined}
                          >
                            <Button
                              size="small"
                              className='tiny'
                              onClick={async () => {
                                try {
                                  await setAppInstalled(true);
                                  setSalesforceStepCompleted(prev => ({ ...prev, appInstalled: true }));
                                } catch (error) {
                                  toastV4Error(error, 'Failed to update app installation status');
                                }
                              }}
                              disabled={!canManageConnection}
                            >
                              Mark as Done
                            </Button>
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Step 2: Connect to Salesforce */}
                    <div className={`salesforce-setup-step ${salesforceStepCompleted.oauthConnected ? 'salesforce-setup-step--completed' : ''}`}>
                      <div className="salesforce-setup-step-header">
                        <div className={`salesforce-setup-step-number ${salesforceStepCompleted.oauthConnected ? 'salesforce-setup-step-number--completed' : ''}`}>
                          2
                        </div>
                        <span className="salesforce-setup-step-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flex: 1 }}>
                          <span>Step 2: Connect to Salesforce</span>
                          {salesforceStepCompleted.oauthConnected ? (
                            <UniversalTag category="DONE" />
                          ) : (
                            <UniversalTag category="PENDING" />
                          )}
                        </span>
                      </div>
                      <div className="salesforce-setup-step-description">
                        Authorize Edulead via OAuth 2.0 to export your contacts and institutions to Salesforce. Required scopes: API access and refresh token for continuous data export.
                      </div>
                      {salesforceStepCompleted.oauthConnected && salesforceConnectionStatus?.connected && (
                        <div style={{ marginTop: 10, marginBottom: 10, paddingLeft: 42, fontSize: 13 }}>
                          <div style={{ marginBottom: 5 }}>
                            <span style={{ display: 'inline-block', width: 120 }}>Organization ID:</span>
                            <span>{salesforceConnectionStatus.account_id}</span>
                          </div>
                          <div style={{ marginBottom: 5 }}>
                            <span style={{ display: 'inline-block', width: 120 }}>Instance URL:</span>
                            <span>{salesforceConnectionStatus.instance_url}</span>
                          </div>
                          <div style={{ marginBottom: 5 }}>
                            <span style={{ display: 'inline-block', width: 120 }}>Connected:</span>
                            <span>{salesforceConnectionStatus.connect_time ? new Date(salesforceConnectionStatus.connect_time).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }) : 'N/A'}</span>
                          </div>
                        </div>
                      )}
                      <div className="salesforce-setup-step-actions">
                        {!salesforceStepCompleted.oauthConnected ? (
                          <>
                            <span
                              data-tooltip-id={!canManageConnection ? 'main-tooltip' : undefined}
                              data-tooltip-content={!canManageConnection ? 'You do not have permission to manage CRM connections' : undefined}
                            >
                              <Button
                                size="small"
                                variation="primary"
                                className='tiny'
                                onClick={async () => {
                                  await connectToSalesforce();
                                }}
                                disabled={isConnectingToSalesforce || !salesforceStepCompleted.appInstalled || !canManageConnection}
                              >
                                {isConnectingToSalesforce ? 'Connecting...' : 'Connect to Salesforce'}
                              </Button>
                            </span>
                            <Button
                              size="small"
                              className='tiny'
                              onClick={async () => {
                                await loadConnectionStatus(true);
                              }}
                              disabled={isRefreshing || !salesforceStepCompleted.appInstalled}
                            >
                              {isRefreshing ? 'Checking...' : 'Check Connection Status'}
                            </Button>
                          </>
                        ) : (
                          <span
                            data-tooltip-id={!canManageConnection ? 'main-tooltip' : undefined}
                            data-tooltip-content={!canManageConnection ? 'You do not have permission to manage CRM connections' : undefined}
                          >
                            <Button
                              size="small"
                              className='tiny'
                              onClick={disconnectFromSalesforce}
                              disabled={!canManageConnection}
                            >
                              Disconnect Salesforce
                            </Button>
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Step 3: Configure Field Mappings (indicator only) */}
                    <div className={`salesforce-setup-step ${salesforceStepCompleted.ncesIdMapped ? 'salesforce-setup-step--completed' : ''}`}>
                      <div className="salesforce-setup-step-header">
                        <div className={`salesforce-setup-step-number ${salesforceStepCompleted.ncesIdMapped ? 'salesforce-setup-step-number--completed' : ''}`}>
                          3
                        </div>
                        <span className="salesforce-setup-step-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flex: 1 }}>
                          <span>Step 3: Configure Field Mappings</span>
                          {salesforceStepCompleted.ncesIdMapped ? (
                            <UniversalTag category="DONE" />
                          ) : (
                            <UniversalTag category="PENDING" />
                          )}
                        </span>
                      </div>
                      <div className="salesforce-setup-step-description">
                        Configure how Edulead fields map to your Salesforce Account fields (for institution matching) and Contact fields (for contact data export). See field mappings below.
                      </div>
                    </div>

                  </div>
                )
              }
            </Card>

            {/* Field Mappings Section - loads independently */}
            {globalContext?.authInfo?.teamId && (
              <div style={{ marginTop: 30 }}>
                <SalesforceFieldMappingsInline
                  key={fieldMappingsKey}
                  teamId={globalContext.authInfo.teamId}
                  onSaveSuccess={handleFieldMappingsSaved}
                  readOnly={!canEditFieldMappings}
                />
              </div>
            )}
          </div>
        </>
      )}
    </GlobalContext.Consumer>
  );
}
